<template>
  <div class="ksr-grid">
    <!-- Card 1: Velocidade — com sparkline embutida -->
    <div class="ksr-card ksr-card-spark" @mouseenter="hover='velocity'" @mouseleave="hover=null">
      <div class="ksr-icon ksr-icon-blue">
        <i class="bx bx-trending-up"></i>
      </div>
      <div class="ksr-body">
        <div class="ksr-label">
          Velocidade
          <span v-if="velocityWindowMin" class="ksr-window-pill">{{ velocityWindowMin }}min</span>
        </div>
        <div class="ksr-value">
          {{ velocityPerMin !== null ? velocityPerMin : '—' }}
          <small>leit/min</small>
        </div>
        <div class="ksr-sub">
          <template v-if="hasRecentActivity">
            atividade na janela atual
          </template>
          <template v-else-if="lastReadAt">
            sem atividade · {{ formatLastRead }}
          </template>
          <template v-else>
            aguardando primeira leitura
          </template>
        </div>
      </div>
      <div class="ksr-spark" v-if="hasRecentActivity && hasSpark">
        <apexchart
          type="area"
          height="46"
          :options="sparkOptions('#556ee6')"
          :series="[{ name: 'Entradas', data: sparkSeries }]"
        />
      </div>
    </div>

    <!-- Card 2: Tempo médio entre leituras — barra horizontal de "rapidez" -->
    <div class="ksr-card">
      <div class="ksr-icon ksr-icon-grey">
        <i class="bx bx-stopwatch"></i>
      </div>
      <div class="ksr-body">
        <div class="ksr-label">Tempo médio entre leituras</div>
        <div class="ksr-value">
          {{ avgIntervalLabel }}
        </div>
        <div class="ksr-speed-bar">
          <div class="ksr-speed-fill" :style="{ width: speedPct + '%' }"></div>
        </div>
        <div class="ksr-speed-caption">{{ speedCaption }}</div>
      </div>
    </div>

    <!-- Card 3: Negadas 24h — donut mini com proporção -->
    <div class="ksr-card">
      <div class="ksr-mini-donut" v-if="denied24h !== null && denied24h > 0">
        <svg viewBox="0 0 36 36" class="ksr-donut-svg">
          <path
            class="ksr-donut-bg"
            d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
          />
          <path
            class="ksr-donut-fill"
            :stroke-dasharray="`${deniedPct}, 100`"
            d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
          />
        </svg>
        <div class="ksr-donut-center">{{ deniedPct }}%</div>
      </div>
      <div class="ksr-icon ksr-icon-red" v-else>
        <i class="bx bx-shield-x"></i>
      </div>
      <div class="ksr-body">
        <div class="ksr-label">Tentativas negadas (24h)</div>
        <div class="ksr-value ksr-value-danger" v-if="denied24h !== null">
          {{ denied24h }}
        </div>
        <div class="ksr-value" v-else>—</div>
        <div class="ksr-sub" v-if="denied24h === 0">Tudo aprovado nas últimas 24h</div>
        <div class="ksr-sub" v-else-if="denied24h > 0">
          {{ deniedPct }}% das leituras totais
        </div>
      </div>
    </div>

    <!-- Card 4: Pico do período — range de hora + count -->
    <div class="ksr-card">
      <div class="ksr-icon ksr-icon-purple">
        <i class="bx bx-trending-up"></i>
      </div>
      <div class="ksr-body">
        <div class="ksr-label">Pico do período</div>
        <div class="ksr-value">
          <span v-if="peak && peak.hour">{{ peak.hour }}</span>
          <span v-else>—</span>
          <span class="ksr-peak-range" v-if="peak && peak.hour_end">
            até {{ peak.hour_end }}
          </span>
        </div>
        <div class="ksr-sub" v-if="peak && peak.count > 0">
          {{ peak.count }} {{ peak.count === 1 ? 'check-in' : 'check-ins' }} · maior fluxo
        </div>
        <div class="ksr-sub" v-else>
          sem leituras suficientes
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'kpi-spark-row',
  props: {
    velocityPerMin: { type: Number, default: null },
    velocityWindowMin: { type: Number, default: null },
    avgIntervalSeconds: { type: Number, default: null },
    denied24h: { type: Number, default: null },
    peak: { type: Object, default: null },
    timeline: { type: Array, default: () => [] },
    totalCheckIns: { type: Number, default: 0 },
    lastReadAt: { type: String, default: null }
  },
  data: () => ({
    hover: null,
    nowTick: 0,
    nowTickInterval: null
  }),
  mounted () {
    this.nowTickInterval = setInterval(() => { this.nowTick++ }, 30000)
  },
  beforeDestroy () {
    if (this.nowTickInterval) clearInterval(this.nowTickInterval)
  },
  computed: {
    hasRecentActivity () {
      return this.velocityPerMin !== null && this.velocityPerMin > 0
    },
    formatLastRead () {
      // eslint-disable-next-line no-unused-expressions
      this.nowTick
      if (!this.lastReadAt) return ''
      const t = new Date(this.lastReadAt.replace ? this.lastReadAt.replace(' ', 'T') : this.lastReadAt).getTime()
      const diff = Math.max(0, Math.floor((Date.now() - t) / 1000))
      if (diff < 60) return 'última agora'
      const m = Math.floor(diff / 60)
      if (m < 60) return `última há ${m}min`
      const h = Math.floor(m / 60)
      return `última há ${h}h`
    },
    /**
     * Sparkline da velocidade — últimos 12 buckets de entradas. Reaproveita
     * a timeline que já carregamos.
     */
    sparkSeries () {
      if (!this.timeline || this.timeline.length === 0) return []
      return this.timeline.slice(-12).map(b => b.in || 0)
    },
    hasSpark () {
      return this.sparkSeries.length >= 3
    },
    avgIntervalLabel () {
      if (this.avgIntervalSeconds === null || this.avgIntervalSeconds === undefined) return '—'
      const s = this.avgIntervalSeconds
      if (s < 60) return `${Math.round(s)}s`
      const m = Math.floor(s / 60)
      const rest = Math.round(s % 60)
      return rest === 0 ? `${m}min` : `${m}min ${rest}s`
    },
    /**
     * "Rapidez" inversa do intervalo: barra cheia quando intervalo é curto.
     * Escala: 0s→100%, 60s→0%. Acima de 60s já considera lento.
     */
    speedPct () {
      const s = this.avgIntervalSeconds
      if (s === null || s === undefined) return 0
      if (s <= 1) return 100
      if (s >= 60) return 5
      return Math.max(5, Math.round(100 - (s / 60) * 100))
    },
    speedCaption () {
      const s = this.avgIntervalSeconds
      if (s === null || s === undefined) return ''
      if (s < 5) return 'Fluxo rápido'
      if (s < 20) return 'Fluxo regular'
      if (s < 60) return 'Fluxo moderado'
      return 'Fluxo lento'
    },
    deniedPct () {
      if (!this.denied24h || !this.totalCheckIns) return 0
      const total = this.denied24h + this.totalCheckIns
      if (total === 0) return 0
      return Math.min(100, Math.round((this.denied24h / total) * 100))
    }
  },
  methods: {
    sparkOptions (color) {
      return {
        chart: {
          sparkline: { enabled: true },
          animations: { enabled: true, speed: 400 }
        },
        colors: [color],
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        tooltip: { enabled: false },
        markers: { size: 0 }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ksr-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.ksr-card {
  position: relative;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  min-height: 110px;

  &:hover {
    border-color: #d4d8e3;
    box-shadow: 0 4px 16px rgba(26, 29, 38, 0.06);
    transform: translateY(-1px);
  }
}

/* Sparkline ocupa metade direita inferior do card */
.ksr-card-spark {
  .ksr-spark {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 60%;
    pointer-events: none;
    opacity: 0.85;
  }
}

.ksr-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-size: 20px;
  flex-shrink: 0;

  &.ksr-icon-blue   { background: rgba(85, 110, 230, 0.12); color: #556ee6; }
  &.ksr-icon-grey   { background: #f0f1f6;                  color: #4a5068; }
  &.ksr-icon-red    { background: rgba(244, 106, 106, 0.12); color: #f46a6a; }
  &.ksr-icon-purple { background: rgba(139, 92, 246, 0.12); color: #8b5cf6; }
}

.ksr-body {
  flex: 1;
  min-width: 0;
}

.ksr-label {
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ksr-window-pill {
  font-size: 9px;
  font-weight: 700;
  color: #556ee6;
  background: rgba(85, 110, 230, 0.1);
  padding: 1px 5px;
  border-radius: 4px;
  letter-spacing: 0.02em;
  text-transform: lowercase;
}

.ksr-value {
  font-size: 26px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  gap: 4px;

  small {
    font-size: 11px;
    font-weight: 500;
    color: #8b90a5;
  }

  &.ksr-value-danger { color: #f46a6a; }
}

.ksr-sub {
  font-size: 11px;
  color: #8b90a5;
  margin-top: 4px;
  font-weight: 500;
}

/* Speed bar (card 2) */
.ksr-speed-bar {
  margin-top: 8px;
  height: 5px;
  background: #f0f1f6;
  border-radius: 99px;
  overflow: hidden;
  position: relative;
}
.ksr-speed-fill {
  height: 100%;
  background: linear-gradient(90deg, #f46a6a 0%, #f1b44c 50%, #34c38f 100%);
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}
.ksr-speed-caption {
  font-size: 11px;
  color: #8b90a5;
  margin-top: 4px;
  font-weight: 500;
}

/* Mini donut (card 3) */
.ksr-mini-donut {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}
.ksr-donut-svg { width: 100%; height: 100%; transform: rotate(0deg); }
.ksr-donut-bg {
  fill: none;
  stroke: rgba(244, 106, 106, 0.15);
  stroke-width: 3;
}
.ksr-donut-fill {
  fill: none;
  stroke: #f46a6a;
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}
.ksr-donut-center {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #f46a6a;
}

/* Peak card — range de hora */
.ksr-peak-range {
  font-size: 13px;
  color: #4a5068;
  font-weight: 600;
  margin-left: 4px;
  letter-spacing: 0;
}

@media (max-width: 1100px) {
  .ksr-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 540px) {
  .ksr-grid { grid-template-columns: 1fr; }
}
</style>
